// React
import { useMemo } from "react";

// Third Party
import moment from "moment";

// MUI
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { GridRenderEditCellParams } from "@mui/x-data-grid-pro";

// Components
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// Helpers
import { and } from "@/Common/helpers/sonar";

// Types
import { TSelectValue } from "@/Layouts/components/InputComponents/SmartSelector/interfaces";
import {
	IReconciliation,
	IReconciliationNoteHistory,
	IReconciliationReason,
	IReconciliationResolution,
} from "@/DataPages/types/inventory/models/reconciliation";

interface IInputEditSelectorCellProps {
	apiRef: React.MutableRefObject<GridApiPremium>;
	params: GridRenderEditCellParams;
	reasonsOptions: IReconciliationReason[];
	resolutionsOptions: IReconciliationResolution[];
	popperProps?: object;
	isLoading?: boolean;
	onOpenAction?: () => void;
	onCloseAction?: () => void;
}

const isValueValid = (value: string, options: { label: string }[]) => {
	if (!value) {
		return false;
	}
	return options.some((option) => option.label === value);
};

const hasValueOtherField = (
	field: string,
	row: IReconciliation,
	reasonsOptions: IReconciliationReason[],
	resolutionsOptions: IReconciliationResolution[]
) => {
	if (field === "reason") {
		const optionsList = getOptionsList("resolution", reasonsOptions, resolutionsOptions);
		return isValueValid(row.resolution, optionsList);
	}
	if (field === "resolution") {
		const optionsList = getOptionsList("reason", reasonsOptions, resolutionsOptions);
		return isValueValid(row.reason, optionsList);
	}
	return false;
};

const someNoteWasCreatedAfterReconciliationDate = (row: IReconciliation) => {
	return row.notes.notes?.some((noteItem: IReconciliationNoteHistory) => moment(noteItem.createdAt).isAfter(row.reconciliationDate));
};

const getOptionsList = (
	field: string,
	reasonsOptions: IReconciliationReason[],
	resolutionsOptions: IReconciliationResolution[]
): { label: string }[] => {
	const optionsMap = new Map<string, (IReconciliationReason | IReconciliationResolution)[]>([
		["reason", reasonsOptions],
		["resolution", resolutionsOptions],
	]);
	const options = optionsMap.get(field) ?? [];
	return options.map((option) => {
		if (field === "reason" && "reason" in option) {
			return { label: option.reason };
		}
		if (field === "resolution" && "resolution" in option) {
			return { label: option.resolution };
		}
		return { label: "" };
	});
};

const getPlaceholder = (field: string) => {
	const placeholderMap = new Map<string, string>([
		["reason", "Select Reason"],
		["resolution", "Select Resolution"],
	]);
	return placeholderMap.get(field) ?? "";
};

const EditCellSelector = (props: IInputEditSelectorCellProps) => {
	const { params, apiRef, popperProps, reasonsOptions, resolutionsOptions, isLoading, onOpenAction, onCloseAction } = props;
	const { id, field, value, row, error } = params;

	const handleReasonChange = (value: TSelectValue) => {
		if (Array.isArray(value)) {
			const updatedSelectedValues = value.filter((v) => "label" in v).map((v) => v.label);
			apiRef.current.setEditCellValue({ id, field, value: updatedSelectedValues, debounceMs: 200 });
		} else if (value && "label" in value) {
			apiRef.current.setEditCellValue({ id, field, value: value.label, debounceMs: 200 });
		}
	};

	const optionsList = useMemo(
		() => getOptionsList(field, reasonsOptions, resolutionsOptions),
		[field, reasonsOptions, resolutionsOptions]
	);

	const isInError = useMemo(() => {
		const inError = and(
			!isValueValid(value, optionsList),
			!hasValueOtherField(field, row, reasonsOptions, resolutionsOptions),
			!someNoteWasCreatedAfterReconciliationDate(row)
		);
		if (apiRef.current.state.editRows?.[id]?.[field]) {
			apiRef.current.state.editRows[id][field].error = inError;
		}
		return inError;
	}, [value, field, optionsList, row, reasonsOptions, resolutionsOptions]);

	return (
		<SmartSelector
			addable={false}
			clearable={false}
			tagsVariant="text"
			showBy="label"
			selectBy="label"
			options={optionsList}
			selected={value}
			loading={isLoading}
			inputProps={{
				placeholder: getPlaceholder(field),
				classes: `[&_.MuiInputBase-root]:!min-h-[26px] [&_.MuiInputBase-root]:!py-[3px] [&_.MuiInputBase-root_.MuiInputBase-input]:!rounded-none !max-w-32`,
			}}
			popperProps={popperProps}
			onChangeAction={handleReasonChange}
			error={error || isInError}
			onOpenAction={onOpenAction}
			onCloseAction={onCloseAction}
		/>
	);
};

export default EditCellSelector;
